import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

export type MetricUnit = "lbs" | "kg";

interface NutrientEntry { amount: number; unit: string; percentDV: number; }

export interface FoodItem {
  id: string;
  name: string;
  grams: number;
  /** Amount as recorded when it is not grams, for example "1 cup" or "250 ml". */
  quantityLabel?: string;
  checked: boolean;
  category: "protein" | "vegetables" | "carbs" | "fats" | "other";
  novaLevel: number;
  healthBenefit: string;
  betterAlternative: string | null;
  nutrients: {
    calories: number; protein: number; carbs: number; fat: number; fiber: number;
    vitamins: Record<string, NutrientEntry>;
    minerals: Record<string, NutrientEntry>;
  } | null;
  nutrients71?: Record<string, number> | null;
  neuralConfidence?: number;
  gutIQ?: number;
  iqGrade?: string;
  loading?: boolean;
}

interface WeeklyQty { section: string; portionPerPlate: string; weeklyKg: number; weeklyLbs: number; estimatedPrice?: number; }
interface ToReach100Item { food: string; reason: string; portionNote: string; weeklyKg: number; weeklyLbs: number; scoreImpact: string; estimatedPrice?: number; }

export interface GroceryAnalysis {
  healthScore: number;
  healthLabel: string;
  goalInsight?: string;
  dailyProteinG?: number;
  familySize?: number;
  goal?: string;
  goalLabel?: string;
  plateYours: { protein: number; vegetables: number; carbs: number; fats: number };
  plateOptimized: { protein: number; vegetables: number; carbs: number; fats: number };
  plateGapAdvice: string;
  totalNutrients: {
    calories: number; protein: number; carbs: number; fat: number; fiber: number;
    vitamins: Record<string, { percentDV: number; status: string }>;
  };
  optimizations: Array<{ item: string; issue: string; suggestion: string; upgrade: string | null; upgradeReason?: string; impact: string }>;
  weeklyQuantities: Record<string, WeeklyQty>;
  toReach100: ToReach100Item[];
  missingNutrients: string[];
  portionGuide: string;
  plateAdvice: string;
}

export interface GutOptimizeData {
  itemScores: Array<{
    name: string;
    category: string;
    gutIQ: number;
    categoryCount: number;
    isPowerhouse: boolean;
    topCategories: Array<{ name: string; score: number; mechanism: string }>;
  }>;
  currentScore: { avgGutIQ: number; maxGutIQ: number; gutActiveItems: number; powerhouses: number; totalItems: number };
  boostedScore: { avgGutIQ: number; gutActiveItems: number; powerhouses: number; totalItems: number };
  scoreDiff: number;
  boostSuggestions: Array<{
    foodName: string;
    gutIQ: number;
    categoryCount: number;
    isPowerhouse: boolean;
    categories: Array<{ name: string; score: number; mechanism: string }>;
  }>;
  swapSuggestions: Array<{
    original: string;
    originalGutIQ: number;
    replacement: string;
    replacementGutIQ: number;
    scoreDiff: number;
    reason: string;
  }>;
  categoryCoverage: Array<{ id: string; name: string; covered: number; icon: string; color: string }>;
}

interface GroceryStore {
  listInput: string;
  setListInput: (v: string) => void;
  defaultGrams: string;
  setDefaultGrams: (v: string) => void;
  unit: MetricUnit;
  setUnit: (v: MetricUnit) => void;
  items: FoodItem[];
  setItems: React.Dispatch<React.SetStateAction<FoodItem[]>>;
  analysis: GroceryAnalysis | null;
  setAnalysis: (v: GroceryAnalysis | null) => void;
  familySize: number;
  setFamilySize: (v: number) => void;
  searching: boolean;
  setSearching: (v: boolean) => void;
  analyzing: boolean;
  setAnalyzing: (v: boolean) => void;
  completing: boolean;
  setCompleting: (v: boolean) => void;
  searchProgress: { done: number; total: number };
  setSearchProgress: React.Dispatch<React.SetStateAction<{ done: number; total: number }>>;
  addingFood: string | null;
  setAddingFood: (v: string | null) => void;
  addedFoods: Set<string>;
  setAddedFoods: React.Dispatch<React.SetStateAction<Set<string>>>;
  optimizeOn: boolean;
  setOptimizeOn: (v: boolean) => void;
  optimizing: boolean;
  setOptimizing: (v: boolean) => void;
  suggestions: Array<{ name: string; reason: string; category: string }>;
  setSuggestions: (v: Array<{ name: string; reason: string; category: string }>) => void;
  gutData: GutOptimizeData | null;
  setGutData: (v: GutOptimizeData | null) => void;
  isBusy: boolean;
  reset: () => void;
  ownerKey: string;
  /** Unowned pre-isolation data found on this device. Never imported automatically. */
  legacy: { listInput: string; itemCount: number } | null;
  recoverLegacy: () => void;
  discardLegacy: () => void;
  replaceDraft: (d: { listInput: string; items: FoodItem[]; familySize: number; unit: MetricUnit }) => void;
}

const GroceryContext = createContext<GroceryStore | null>(null);

export const LEGACY_GROCERY_KEYS = ["plate-os-grocery-draft", "plate-os-grocery-items"] as const;
export function ownedGroceryKey(ownerKey: string, name: "draft" | "items" | "unit" | "family") {
  return `plate-os:${ownerKey}:grocery-${name}`;
}
const canPersist = (ownerKey: string) => ownerKey !== "loading" && ownerKey !== "anonymous";
function readJson<T>(key: string): T | null {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : null; } catch { return null; }
}
function cleanItems(v: unknown): FoodItem[] {
  if (!Array.isArray(v)) return [];
  return (v as FoodItem[])
    .filter(i => i && typeof i === "object" && !i.loading && typeof i.id === "string" && typeof i.name === "string" && i.name.trim())
    .map(i => {
      const label = typeof i.quantityLabel === "string" ? i.quantityLabel.trim() : "";
      const labeled = label.length > 0;
      const grams = Number.isFinite(Number(i.grams)) && Number(i.grams) > 0 ? Number(i.grams) : labeled ? 0 : 100;
      return { ...i, loading: false, checked: i.checked !== false, grams, quantityLabel: labeled ? label : undefined };
    });
}

export function GroceryProvider({ children, ownerKey = "anonymous" }: { children: ReactNode; ownerKey?: string }) {
  const persist = canPersist(ownerKey);
  const [listInput, setListInput] = useState(() => {
    if (!persist) return "";
    try { return localStorage.getItem(ownedGroceryKey(ownerKey, "draft")) ?? ""; } catch { return ""; }
  });
  const persistedSetListInput = useCallback((v: string) => {
    setListInput(v);
    if (persist) try { localStorage.setItem(ownedGroceryKey(ownerKey, "draft"), v); } catch {}
  }, [ownerKey, persist]);
  const [defaultGrams, setDefaultGrams] = useState("100");
  const [unit, setUnit] = useState<MetricUnit>(() => {
    if (!persist) return "lbs";
    try { return localStorage.getItem(ownedGroceryKey(ownerKey, "unit")) === "kg" ? "kg" : "lbs"; } catch { return "lbs"; }
  });
  const [items, setItems] = useState<FoodItem[]>(() => persist ? cleanItems(readJson(ownedGroceryKey(ownerKey, "items"))) : []);
  useEffect(() => {
    if (!persist) return;
    try {
      const toSave = items.filter(i => !i.loading);
      if (toSave.length === 0) localStorage.removeItem(ownedGroceryKey(ownerKey, "items"));
      else localStorage.setItem(ownedGroceryKey(ownerKey, "items"), JSON.stringify(toSave));
    } catch {}
  }, [items, ownerKey, persist]);
  const [familySize, setFamilySizeState] = useState(() => {
    const n = persist ? Number(readJson<number>(ownedGroceryKey(ownerKey, "family"))) : 1;
    return Number.isFinite(n) && n >= 1 && n <= 12 ? Math.round(n) : 1;
  });
  const setFamilySize = useCallback((v: number) => {
    setFamilySizeState(v);
    if (persist) try { localStorage.setItem(ownedGroceryKey(ownerKey, "family"), JSON.stringify(v)); } catch {}
  }, [ownerKey, persist]);

  const [legacy, setLegacy] = useState<{ listInput: string; itemCount: number } | null>(() => {
    if (!persist) return null;
    try {
      const d = localStorage.getItem(LEGACY_GROCERY_KEYS[0]) ?? "";
      const its = cleanItems(readJson(LEGACY_GROCERY_KEYS[1]));
      return d.trim() || its.length ? { listInput: d, itemCount: its.length } : null;
    } catch { return null; }
  });
  const discardLegacy = useCallback(() => {
    try { LEGACY_GROCERY_KEYS.forEach(k => localStorage.removeItem(k)); } catch {}
    setLegacy(null);
  }, []);
  const recoverLegacy = useCallback(() => {
    if (!persist) return;
    let d = ""; let its: FoodItem[] = [];
    try { d = localStorage.getItem(LEGACY_GROCERY_KEYS[0]) ?? ""; its = cleanItems(readJson(LEGACY_GROCERY_KEYS[1])); } catch {}
    if (d.trim()) setListInput(prev => {
      const next = prev.trim() ? `${prev}\n${d}` : d;
      try { localStorage.setItem(ownedGroceryKey(ownerKey, "draft"), next); } catch {}
      return next;
    });
    if (its.length) setItems(prev => [...prev, ...its.map(i => ({ ...i, id: crypto.randomUUID() }))]);
    discardLegacy();
  }, [ownerKey, persist, discardLegacy]);

  const [analysis, setAnalysis] = useState<GroceryAnalysis | null>(null);
  const [searching, setSearching] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });
  const [addingFood, setAddingFood] = useState<string | null>(null);
  const [addedFoods, setAddedFoods] = useState<Set<string>>(new Set());
  const [optimizeOn, setOptimizeOn] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ name: string; reason: string; category: string }>>([]);
  const [gutData, setGutData] = useState<GutOptimizeData | null>(null);

  const persistedSetUnit = useCallback((v: MetricUnit) => {
    setUnit(v);
    if (persist) try { localStorage.setItem(ownedGroceryKey(ownerKey, "unit"), v); } catch {}
  }, [ownerKey, persist]);

  const replaceDraft = useCallback((d: { listInput: string; items: FoodItem[]; familySize: number; unit: MetricUnit }) => {
    persistedSetListInput(d.listInput ?? "");
    setItems(cleanItems(d.items));
    setFamilySize(d.familySize >= 1 ? d.familySize : 1);
    persistedSetUnit(d.unit === "kg" ? "kg" : "lbs");
    setAnalysis(null);
    setGutData(null);
  }, [persistedSetListInput, setFamilySize, persistedSetUnit]);

  const isBusy = searching || analyzing || completing || optimizing;

  const reset = useCallback(() => {
    setListInput("");
    try {
      (["draft", "items", "family", "unit"] as const).forEach(n => localStorage.removeItem(ownedGroceryKey(ownerKey, n)));
    } catch {}
    setDefaultGrams("100");
    setUnit("lbs");
    setItems([]);
    setAnalysis(null);
    setFamilySizeState(1);
    setSearching(false);
    setAnalyzing(false);
    setCompleting(false);
    setSearchProgress({ done: 0, total: 0 });
    setAddingFood(null);
    setAddedFoods(new Set());
    setOptimizeOn(false);
    setOptimizing(false);
    setSuggestions([]);
    setGutData(null);
  }, [ownerKey]);

  return (
    <GroceryContext.Provider value={{
      listInput, setListInput: persistedSetListInput,
      defaultGrams, setDefaultGrams,
      unit, setUnit: persistedSetUnit,
      items, setItems,
      analysis, setAnalysis,
      familySize, setFamilySize,
      searching, setSearching,
      analyzing, setAnalyzing,
      completing, setCompleting,
      searchProgress, setSearchProgress,
      addingFood, setAddingFood,
      addedFoods, setAddedFoods,
      optimizeOn, setOptimizeOn,
      optimizing, setOptimizing,
      suggestions, setSuggestions,
      gutData, setGutData,
      isBusy,
      reset,
      ownerKey, legacy, recoverLegacy, discardLegacy, replaceDraft,
    }}>
      {children}
    </GroceryContext.Provider>
  );
}


export function useGroceryStore() {
  const ctx = useContext(GroceryContext);
  if (!ctx) throw new Error("useGroceryStore must be used inside GroceryProvider");
  return ctx;
}
